import './Nav.css';
import NavGroup from './NavGroup';
import { NavLink, useHistory, useLocation, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { userLogout, switchAccount, removeAccount } from '../store/user.js';
import { getUserInitials as getInitials } from '../helpers/accountHelpers';
import { MAX_ACCOUNTS, ACCOUNT_CAP_MESSAGE } from '../constants/accounts';
import AccountRow from '../components/account/AccountRow';
import { adminLogout, stopImpersonation, IMPERSONATION_END_REASONS } from '../store/admin.js';
import { Typography } from '@mui/material';
import 'fontsource-roboto';
import JRLogo from '../assets/marketing/Jane Rothe/janeRotheLogoBlack.webp';

import React, { useEffect, useContext, useState, useMemo } from 'react';
import LogoutIcon from '@mui/icons-material/Logout';
import { User, Settings, UserPlus } from 'lucide-react';
import Dropdown from '../components/ui/dropdown';
import Avatar from '../components/ui/avatar';
import { useOnboardingImages } from '../appComponents/onboarding/hooks/useOnboardingImages';
// import zIndex from '@mui/material/styles/zIndex';

/** Nav bar for the App.  It changes depending on whether user is logged in */
function Nav() {
	/** Get Current user from redux instead of useContext */
	const currentUser = useSelector((st) => st.user.currentUser);
	const currentAdmin = useSelector((st) => st.admin.currentAdmin);
	const isImpersonating = useSelector((st) => st.admin.isImpersonating);
	const history = useHistory();
	const dispatch = useDispatch();

	// Use the same image hook as the profile page
	const clientImages = useOnboardingImages(currentUser);

	// Get primary headshot using the same method as ClientDetailsEnhanced
	const getPrimaryHeadshot = () => {
		if (!clientImages.getImagesByType) return null;

		const headshotImages = clientImages.getImagesByType('headshot');
		if (!headshotImages || headshotImages.length === 0) return null;

		// Find primary image or use first available
		const primaryImage =
			headshotImages.find((img) => img.isPrimary) || headshotImages[0];
		return primaryImage?.imageUrl || null;
	};

	const primaryHeadshot = getPrimaryHeadshot();

	// Get user initials for avatar
	const getUserInitials = () => getInitials(currentUser);

	// Multi-account switcher (#168)
	const accounts = useSelector((st) => st.user.accounts) || [];
	const activeUsername = useSelector((st) => st.user.activeUsername);
	const hasMultipleAccounts = accounts.length > 1;
	const atAccountCap = accounts.length >= MAX_ACCOUNTS;

	const handleSwitchAccount = (username) => {
		if (username !== activeUsername) dispatch(switchAccount(username));
		// Land somewhere every account can see.
		history.push('/home');
	};

	const handleSignOutActive = () => {
		dispatch(removeAccount(activeUsername));
		history.push('/home');
	};

	// define links for when a user is signed in
	const signedInLinks = [
		{
			name: 'logout',
			icon: <LogoutIcon sx={{ color: 'rgb(156 163 175)' }} />,
			path: currentUser && currentUser.username ? 'logout' : 'admin/login',
			logout: true,
			style:
				'h-9  sm:h-auto inline-block bg-white py-2 px-4 border border-gray-200 rounded-md text-sm iphone:text-sm text-base font-medium pink-700 hover:bg-gray-50 text-gray-500 hover:text-gray-900 shadow-sm',
			// Modify the css thats why the snap of the Nav.test.js.snap and NavGroup.test.js.snap file mismatched and fail the test cases
		},
	];

	// define links for when a user is signed out
	const signedOutLinks = [
		{
			name: 'Sign in',
			path: 'login',
			style:
				'inline-block bg-white py-2 px-3 border border-gray-200 rounded-md text-xs iphone:text-sm sm:text-base font-medium pink-700 hover:bg-gray-50 text-gray-500 hover:text-gray-900 shadow-sm',
			// Modify the css thats why the snap of the Nav.test.js.snap and NavGroup.test.js.snap file mismatched and fail the test cases
		},
		{
			name: 'Sign up',
			path: 'signup',
			style:
				'inline-block bg-primary py-2 px-3 border border-transparent rounded-md text-xs iphone:text-sm sm:text-base font-medium text-white hover:bg-opacity-80',
			// Modify the css thats why the snap of the Nav.test.js.snap and NavGroup.test.js.snap file mismatched and fail the test cases
		},
	];
	const links =
		(currentUser && currentUser.username) ||
		(currentAdmin && currentAdmin.username)
			? signedInLinks
			: signedOutLinks;


	// Always show profile dropdown for logged-in users - let OnboardingProtectedRoute handle redirection
	const showProfileDropdown = currentUser && currentUser.username;

	return (
		// <div className="top-0 z-10 nav_wrapper block fixed left-0 right-0 top-0 z-50 bg-white shadow">
		<div className='nav_wrapper block bg-white shadow-md border-b border-gray-200' data-testid='nav-wrapper'>
			<div className={`max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 `}>
				<div
					expand='md'
					className={` ${
						currentUser && currentUser.username ? 'py-1.5 ' : 'sm:py-4 py-3'
					} w-full flex items-center justify-between `}>
					<NavLink exact to='/' color='green' className='navbar-brand z-40'>
						<span className='sr-only'>Jane Logo</span>
						<img
							className='h-16 sm:h-20 w-auto m-0 z-40'
							src={JRLogo}
							alt='Jane Logo'
						/>
					</NavLink>

					{/* Right side navigation */}
					<div className='flex items-center space-x-4 z-40'>
						{/* Profile Dropdown - Always shown for authenticated users */}
						{currentUser && currentUser.username && (
							<Dropdown
								width='w-72'
								trigger={
									<div
										className='flex items-center cursor-pointer hover:bg-gray-50 rounded-md p-2 transition-colors'
										data-testid='account-menu-trigger'>
										<Avatar className='h-8 w-8 border border-gray-200'>
											{primaryHeadshot ? (
												<Avatar.Image
													src={primaryHeadshot}
													alt={`${currentUser.firstName} ${currentUser.lastName}`}
												/>
											) : (
												<Avatar.Fallback className='text-sm font-medium bg-blue-100 text-blue-600'>
													{getUserInitials()}
												</Avatar.Fallback>
											)}
										</Avatar>
										<span className='ml-2 text-sm font-medium text-gray-700 hidden sm:block'>
											{currentUser.firstName} {currentUser.lastName}
										</span>
									</div>
								}>
								{/* Always show Profile option for logged-in users - OnboardingProtectedRoute handles redirection */}
								<Dropdown.Item closeOnClick onClick={() => history.push('/profile')}>
									<div className='flex items-center'>
										<User className='mr-2 h-4 w-4' />
										<span>My Profile</span>
									</div>
								</Dropdown.Item>
								<Dropdown.Separator />
								{/* Account switcher (#168) — user accounts only, never during impersonation */}
								{!isImpersonating && hasMultipleAccounts && (
									<div role='group' aria-label='Switch account' data-testid='account-switcher'>
										<div className='px-4 pt-1 pb-1 text-xs font-medium uppercase tracking-wide text-gray-500'>
											Accounts
										</div>
										{accounts.map((account) => (
											<Dropdown.Item
												key={account.username}
												closeOnClick
												aria-current={account.username === activeUsername ? 'true' : undefined}
												onClick={() => handleSwitchAccount(account.username)}>
												<AccountRow
													account={account}
													active={account.username === activeUsername}
												/>
											</Dropdown.Item>
										))}
									</div>
								)}
								{!isImpersonating && (
									<Dropdown.Item
										closeOnClick
										disabled={atAccountCap}
										title={atAccountCap ? ACCOUNT_CAP_MESSAGE : undefined}
										onClick={() => history.push('/login')}>
										<div className='flex items-center'>
											<UserPlus className='mr-2 h-4 w-4' />
											<span>Add account</span>
										</div>
										{atAccountCap && (
											<p className='m-0 mt-1 text-xs text-gray-400'>
												Maximum of {MAX_ACCOUNTS} accounts
											</p>
										)}
									</Dropdown.Item>
								)}
								{!isImpersonating && <Dropdown.Separator />}
								{!isImpersonating && hasMultipleAccounts && (
									<Dropdown.Item closeOnClick onClick={handleSignOutActive}>
										<div className='flex items-center'>
											<LogoutIcon className='mr-2 h-4 w-4' />
											<span>Sign out of this account</span>
										</div>
									</Dropdown.Item>
								)}
								<Dropdown.Item closeOnClick onClick={async () => {
									if (isImpersonating) {
										// Exit impersonation — stopImpersonation handlers in user.js restore/clear user state
										await dispatch(stopImpersonation(IMPERSONATION_END_REASONS.MANUAL));
										history.push('/admin/clients');
									} else {
										// Sign out of every account - clear both user and admin
										dispatch(userLogout());
										dispatch(adminLogout());
										history.push('/login');
									}
								}}>
									<div className='flex items-center'>
										<LogoutIcon className='mr-2 h-4 w-4' />
										<span>
											{!isImpersonating && hasMultipleAccounts
												? 'Sign out of all accounts'
												: 'Logout'}
										</span>
									</div>
								</Dropdown.Item>
							</Dropdown>
						)}

						{/* Regular navigation for signed out users */}
						{!currentUser && <NavGroup links={links} />}
					</div>
				</div>
			</div>
		</div>
	);
}

export default Nav;
